// src/utils/activityModel.js — Pass 47D: staff work is not "instructor-only".
// TWO separate axes, both additive to the existing canonical schedule model:
//   · scheduleEntryType (existing): client_booking | travel | admin | training | meeting | blocked | other
//   · activityType (new, finer): what work was actually performed
//   · workCapabilities (per season on the member): what a person CAN do
// Commercial relationship (School/Collective) stays a THIRD, separate axis (category).
import { isClientBooking } from './scheduleModel';
import { resolveCurrentTeamState, resolveInstructorCategory, TEAM_STATE, CURRENT_SEASON_ID, CATEGORY } from './instructorCategory';

export const ACTIVITY = { LESSON: 'lesson', DRIVING: 'driving', CONCIERGE: 'concierge', AIRPORT: 'airport_transfer', OFFICE: 'office', ADMIN: 'admin', TRAINING: 'training', MEETING: 'meeting', TIMEOFF: 'timeoff', OTHER: 'other' };
export const ACTIVITY_LABEL = { lesson: 'Lesson', driving: 'Driving', concierge: 'Concierge', airport_transfer: 'Airport transfer', office: 'Office', admin: 'Admin / Ops', training: 'Training', meeting: 'Meeting', timeoff: 'Time off', other: 'Other' };
export const CAPABILITY = { TEACHING: 'teaching', DRIVING: 'driving', CONCIERGE: 'concierge', ADMIN: 'admin' };
export const CAPABILITIES = ['teaching', 'driving', 'concierge', 'admin'];

// Resolve the finer activity: explicit field wins; else derive from entry type + text.
export function deriveActivityType(booking = {}) {
  if (booking.activityType) return booking.activityType;
  const t = `${booking.notes || ''} ${booking.requesterName || ''} ${booking.classificationFamily || ''}`.toLowerCase();
  if (isClientBooking(booking)) return ACTIVITY.LESSON;
  const et = booking.scheduleEntryType;
  if (/concierge/.test(t)) return ACTIVITY.CONCIERGE;
  if (et === 'travel') { if (/airport/.test(t)) return ACTIVITY.AIRPORT; return ACTIVITY.DRIVING; }
  if (et === 'admin') return /concierge/.test(t) ? ACTIVITY.CONCIERGE : ACTIVITY.ADMIN;
  if (et === 'training') return ACTIVITY.TRAINING;
  if (et === 'meeting') return ACTIVITY.MEETING;
  if (et === 'blocked') return ACTIVITY.TIMEOFF;
  return ACTIVITY.OTHER;
}

// A person's work capabilities for a season. Explicit list wins; else a sensible
// default: a teaching-category member can teach; a driver can drive.
export function getCapabilities(member = {}, season) {
  const sm = season && member.seasonMemberships && member.seasonMemberships[season];
  if (sm && Array.isArray(sm.workCapabilities) && sm.workCapabilities.length) return sm.workCapabilities;
  if (member.jobFunction === 'driver' || member.isTeachingInstructor === false) return [CAPABILITY.DRIVING];
  // Pass 59B3 — capability FALLBACK for members without explicit workCapabilities. Use the CANONICAL category
  // resolver (understands `sas_school`/`sas_collective` → school/collective), NOT the raw `sm.category` string
  // (comparing raw against 'school'/'collective' dropped every SAS-School teacher whose category is stored as
  // `sas_school`). A School/Collective member (isTeachingInstructor not explicitly false) is teaching-capable.
  const cat = resolveInstructorCategory(member, { season });
  if (cat === CATEGORY.SCHOOL || cat === CATEGORY.COLLECTIVE) return [CAPABILITY.TEACHING];
  return [];
}
export const hasCapability = (member, cap, season) => getCapabilities(member, season).includes(cap);
export const canTeach = (member, season) => hasCapability(member, CAPABILITY.TEACHING, season);
export const canDrive = (member, season) => hasCapability(member, CAPABILITY.DRIVING, season);
export const canConcierge = (member, season) => hasCapability(member, CAPABILITY.CONCIERGE, season);

// Pass 49B §20: the canonical scheduleEntryType for a non-lesson Add-Work activity. Keeps
// non-lesson work inside the existing bookings/schedule-event architecture — NO new parallel
// collections (workTasks/staffEvents/conciergeEvents/drivingSchedule). A lesson is created
// through normal booking creation, not Add Work.
export function scheduleEntryTypeForActivity(activityType) {
  switch (activityType) {
    case ACTIVITY.DRIVING:
    case ACTIVITY.AIRPORT: return 'travel';   // Driving/Airport → travel
    case ACTIVITY.ADMIN:
    case ACTIVITY.OFFICE: return 'admin';      // Admin / Ops → admin
    case ACTIVITY.TRAINING: return 'training';
    case ACTIVITY.MEETING: return 'meeting';
    case ACTIVITY.TIMEOFF: return 'blocked';
    case ACTIVITY.CONCIERGE:                    // Concierge → other (no dedicated entry type)
    default: return 'other';
  }
}

// The non-lesson activities offered by the Add Work action (lesson goes through booking flow).
export const ADD_WORK_ACTIVITIES = [ACTIVITY.DRIVING, ACTIVITY.CONCIERGE, ACTIVITY.ADMIN, ACTIVITY.TRAINING, ACTIVITY.MEETING, ACTIVITY.OTHER];

// §14/§3 who may be offered for a given activity's assignment. Two gates:
//   1. capability (teaching/driving/concierge/admin) — NOT commercial category.
//   2. for the CURRENT (writable) season, participation MUST be confirmed-current — a
//      roster-review person (participation=unknown) is NEVER assignable, even if capable.
//      Historical seasons carry no participation, so only the capability gate applies.
export function eligibleForActivity(members, activityType, season) {
  const cap = activityType === ACTIVITY.LESSON ? CAPABILITY.TEACHING
    : activityType === ACTIVITY.DRIVING || activityType === ACTIVITY.AIRPORT ? CAPABILITY.DRIVING
    : activityType === ACTIVITY.CONCIERGE ? CAPABILITY.CONCIERGE
    // Pass 49B §9: Admin/Office require the ADMIN capability (not everyone). Training/Meeting/
    // Other remain open to any confirmed-current operational staff.
    : activityType === ACTIVITY.ADMIN || activityType === ACTIVITY.OFFICE ? CAPABILITY.ADMIN : null;
  const capable = members.filter((m) => (cap ? hasCapability(m, cap, season) : true));
  if (season !== CURRENT_SEASON_ID) return capable;
  return capable.filter((m) => resolveCurrentTeamState(m, season) === TEAM_STATE.CURRENT);
}

// True iff this person can be assigned a client lesson in the season (teaching-capable, and
// confirmed-current for the current season). The canonical lesson-capacity test.
export function isLessonCapacity(member, season) {
  return eligibleForActivity([member], ACTIVITY.LESSON, season).length > 0;
}
